// opd-nav.js — the SINGLE client-side-navigation choke point for the OPD SPA.
//
// Every internal navigation (programmatic `location.href =` assignments and
// internal <a> anchors) routes through navTo() / linkProps() here. react-router
// stays imported ONLY in App.jsx (which registers its navigate fn) and here —
// screens never import the router.
//
// SHIPS DARK (AGENTS.md §2): a default-OFF kill switch. With the flag off EVERY
// call degrades to exactly today's full-reload behaviour (`location.href = url`),
// so this whole batch is inert under full reload and byte-identical to today.
// The flag flips per-environment only after verification — never as part of an
// unrelated change.
//
// Enable locally with `localStorage.setItem('opd.spaNav','1')`, or per-build via
// the Vite env var VITE_OPD_SPA_NAV=1.

// Read once at module load. `navTo`/`linkProps` are pure passthroughs while off.
// localStorage 'opd.spaNav' is BOTH the local enable ('1') AND the emergency
// runtime kill-switch ('0' forces full-reload nav even on a build where the
// env flag baked it on — set it in the console + hard reload, no redeploy).
const SPA_NAV_ON = (() => {
  try {
    const local = window.localStorage.getItem('opd.spaNav');
    if (local === '0') return false;
    if (local === '1') return true;
    return import.meta.env?.VITE_OPD_SPA_NAV === '1';
  } catch { return false; }
})();

// The staff route set (design §4). SPA nav applies ONLY to these — anon/kiosk/
// magic-link surfaces stay full-reload on their Express bootstrap, and navTo/
// linkProps fall through to location.href for any non-staff path. Prefix match
// on the pathname only (query/hash stripped first).
const STAFF_PREFIXES = ['/queue', '/reception', '/nurse', '/ward', '/patients', '/reports',
  '/schedule', '/settings', '/tasks', '/messaging', '/spaces',
  '/automations', '/admin/', '/consult/'];

/** True when `href`'s pathname is one of the converted staff routes. */
export function isStaffPath(href) {
  const path = String(href || '').split(/[?#]/)[0];
  return STAFF_PREFIXES.some((p) => path === p.replace(/\/$/, '') || path.startsWith(p));
}

let _navigate = null;
/** App.jsx's <NavBridge/> registers react-router's navigate here at mount. */
export function registerNavigate(fn) { _navigate = fn; }

// ── Route chunk prefetch ───────────────────────────────────────────────────
// Each staff screen is a lazy() chunk (App.jsx SCREENS[].load). Under SPA nav a
// click that lands on an un-cached chunk shows a full-screen boot splash while
// the chunk downloads — the user gets no per-pill feedback and often re-taps
// ("takes a couple of clicks to get in"). Warming the chunk on pointer-ENTER /
// pointer-DOWN (before the click completes) means the module is already resolved
// by the time ScreenBoot mounts, so the destination appears near-instantly.
// App.jsx registers { pathname: load } here; AppShell calls prefetchRoute() from
// the nav links' pointer handlers. Each load() fires at most once (deduped).
let _prefetch = null;      // { '/schedule': () => import(...), ... }
const _warmed = new Set(); // pathnames already kicked off
/** App.jsx registers the path→load map (built from its SCREENS table). */
export function registerPrefetch(map) { _prefetch = map || null; }
/**
 * Warm the lazy chunk for `href`'s staff route. No-op when SPA nav is off (a
 * full reload discards the warmed module anyway), for non-staff paths, or once
 * already warmed. Best-effort — a failed prefetch is swallowed; the real load in
 * ScreenBoot surfaces any genuine error.
 */
export function prefetchRoute(href) {
  if (!SPA_NAV_ON || !_prefetch || !isStaffPath(href)) return;
  const path = String(href || '').split(/[?#]/)[0];
  if (_warmed.has(path)) return;
  const load = _prefetch[path];
  if (typeof load !== 'function') return;
  _warmed.add(path);
  try { Promise.resolve(load()).catch(() => {}); } catch { /* ignore */ }
}

/**
 * Replaces `window.location.href = url` for internal navigation.
 * Flag on + a registered navigate + a staff path → SPA navigate (no reload).
 * Otherwise → today's full-page load (location.href / location.replace).
 */
export function navTo(href, { replace = false } = {}) {
  if (SPA_NAV_ON && _navigate && isStaffPath(href)) { _navigate(href, { replace }); return; }
  if (replace) window.location.replace(href); else window.location.href = href;
}

/**
 * Spread onto an internal <a>: keeps the real href (so middle-click / cmd-click /
 * a11y / "copy link" all still work), and intercepts ONLY plain left-clicks.
 * With the flag off (or a non-staff href) it returns { href } only — a normal
 * anchor, i.e. today's full-reload behaviour.
 */
export function linkProps(href) {
  if (!SPA_NAV_ON || !isStaffPath(href)) return { href };
  return {
    href,
    onClick: (e) => {
      // Let the browser handle modified clicks (new tab/window), non-left
      // buttons, and anything a nearer handler already claimed.
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      navTo(href);
    },
  };
}
